import type { JSX } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionContentIndent,
    AccordionDescriptionDetails,
    AccordionDescriptionList,
    AccordionDescriptionTerm,
    AccordionItem,
    AccordionLink,
    AccordionTrigger,
    Heading,
} from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { Process } from '@common/interfaces';
import type { IProcessActor } from '@common/interfaces/api/processesApi';
import type { ProcessActorProfession } from '@common/interfaces/processActors';
import type { IOrganization } from '@frontend/components/interfaces';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getProcessActors } from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useUserLocalStorage } from '@frontend/utils/hooks/useUserLocalStorage';
import type { EditOption } from './CareProvidersTeamEditModal';
import { CareProvidersTeamEditModal } from './CareProvidersTeamEditModal';

export interface IProcessActorOrganization extends IProcessActor {
    organization: IOrganization;
}

type CareProvidersTeamProps = {
    editOptions: EditOption[];
    editTitle?: string;
    isEditable: boolean;
    process: Process;
    processActorProfessionOrder?: Partial<{ [key in ProcessActorProfession]: number }>;
    title: string;
};

export function CareProvidersTeam(props: Readonly<CareProvidersTeamProps>): JSX.Element | null {
    const { editOptions, editTitle, isEditable, process, processActorProfessionOrder, title } = props;
    const abortSignal = useAbortSignal();
    const [accordionValue, setAccordionValue] = useUserLocalStorage('careProvidersTeam_accordion', [
        'care-providers-team',
    ]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [processActorOrganizations, setProcessActorOrganizations] = useState<IProcessActorOrganization[]>([]);

    useEffect(() => {
        loadProcessActors();
    }, [process.id]);

    if (!processActorOrganizations.length) {
        return null;
    }

    return (
        <>
            <Heading
                as="h2"
                fontWeight="medium"
                size="small"
                sx={{ mb: 1, ml: 3 }}
            >
                {title}
            </Heading>
            <Accordion
                defaultValue={accordionValue}
                onValueChange={setAccordionValue}
                type="multiple"
            >
                <AccordionItem value="care-providers-team">
                    <AccordionTrigger icon="patients">
                        {getSelectedLanguage().patient.careProvidersTeam}
                    </AccordionTrigger>
                    <AccordionContent>
                        <AccordionContentIndent>
                            <AccordionDescriptionList>
                                {processActorOrganizations.map((processActor) => (
                                    <Fragment key={processActor.id}>
                                        <AccordionDescriptionTerm>
                                            {getSelectedLanguage().processActorProfession[processActor.profession]}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {processActor.organization.name}
                                            {processActor.organization.phoneNumber && (
                                                <>
                                                    <br />
                                                    {processActor.organization.phoneNumber}
                                                </>
                                            )}
                                            {processActor.organization.emailAddressRequestInbox && (
                                                <>
                                                    <br />
                                                    {processActor.organization.emailAddressRequestInbox}
                                                </>
                                            )}
                                        </AccordionDescriptionDetails>
                                    </Fragment>
                                ))}
                                {isEditable && (
                                    <>
                                        <AccordionLink
                                            onClick={() => setIsEditModalOpen(true)}
                                            variant="button"
                                        >
                                            {getSelectedLanguage().patient.editCareProvidersTeam}
                                        </AccordionLink>
                                        {isEditModalOpen && (
                                            <CareProvidersTeamEditModal
                                                editOptions={editOptions}
                                                initialProcessActors={processActorOrganizations.map((item) => ({
                                                    organization: item.organization,
                                                    profession: item.profession,
                                                }))}
                                                onClose={() => setIsEditModalOpen(false)}
                                                onSubmit={async () => {
                                                    setIsEditModalOpen(false);
                                                    await loadProcessActors();
                                                }}
                                                processId={process.id}
                                                title={editTitle}
                                            />
                                        )}
                                    </>
                                )}
                            </AccordionDescriptionList>
                        </AccordionContentIndent>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </>
    );

    async function mapProcessActorOrganization(processActor: IProcessActor): Promise<IProcessActorOrganization> {
        const organization = await getOneOrganization(processActor.organizationId, abortSignal);

        return {
            ...processActor,
            organization,
        };
    }

    async function loadProcessActors(): Promise<void> {
        const { items: processActors } = await getProcessActors(process.id, undefined, abortSignal);
        let nextProcessActorOrganizations = await Promise.all(processActors.map(mapProcessActorOrganization));
        if (processActorProfessionOrder) {
            nextProcessActorOrganizations = orderBy(
                nextProcessActorOrganizations,
                (processActorOrganization) => processActorProfessionOrder[processActorOrganization['profession']]
            );
        }

        setProcessActorOrganizations(nextProcessActorOrganizations);
    }
}
